import React, { useState, useEffect } from 'react';
import { Container, Card, ListGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, off } from '@firebase/database';
import './Chat.css';
import BackToHome from '../components/BackToHome';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function formatTime(ts) {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString();
}

const ChatList = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      setError('Please login to view your chats');
      setLoading(false);
      return;
    }

    const userChatsRef = ref(database, `user_chats/${currentUser.uid}`);
    const listener = onValue(userChatsRef, (snapshot) => {
      if (snapshot.exists()) {
        const chatsData = snapshot.val();
        const chatsArray = Object.entries(chatsData).map(([chatId, chat]) => ({
          id: chatId,
          ...chat,
        })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setChats(chatsArray);
      } else {
        setChats([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading chats:', error);
      setError('Failed to load chats');
      setLoading(false);
    });

    return () => {
      off(userChatsRef, listener);
    };
  }, [currentUser]);

  const handleChatClick = (chatId) => {
    navigate(`/chat/${chatId}`);
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading chats...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4 chat-list-container">
      <BackToHome />
      <h2 className="mb-4">Inbox</h2>
      {chats.length === 0 ? (
        <Alert variant="info">
          No active chats. Start chatting by viewing an ad and clicking "Chat with Seller"!
        </Alert>
      ) : (
        <Card className="chat-inbox-card">
          <ListGroup variant="flush">
            {chats.map((chat) => (
              <ListGroup.Item
                key={chat.id}
                action
                onClick={() => handleChatClick(chat.id)}
                className="d-flex align-items-center chat-inbox-item"
              >
                <div className="chat-inbox-avatar me-3">
                  {getInitials(chat.otherUserName)}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{chat.adTitle}</h6>
                    <small className="text-muted">{formatTime(chat.timestamp)}</small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span className="text-muted small inbox-last-message">{chat.lastMessage}</span>
                    {chat.unreadCount > 0 && (
                      <Badge bg="primary" pill>
                        {chat.unreadCount}
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted small inbox-other-user">{chat.otherUserName}</div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}
    </Container>
  );
};

export default ChatList; 